<template>
    <v-menu
        v-model="menu"
        :close-on-content-click="false"
        :nudge-width="200"
        max-width="350px"
        offset-x
    >
        <template v-slot:activator="{ on, attrs }">
            {{ teamName }} {{ subgroupName }}
            <v-btn v-bind="attrs" v-on="on" icon v-if="!disabled">
                <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
        </template>

        <v-card class="kimsa-elevation">

            <v-row class="px-3">
                <v-col cols="12">
                    {{ $t('common.team') }}
                    <v-divider></v-divider>
                </v-col>
                <v-col cols="12">
                    <kimsa-combobox
                        :placeholder="$t('common.select_team')"
                        outlined dense
                        :items="teams"
                        @change="team = $event;"
                        :value="team"
                    />
                </v-col>
                <v-col cols="12">
                    <kimsa-combobox
                        :placeholder="$t('common.select_subgroup')"
                        outlined dense
                        :items="subgroups"
                        @change="subgroup = $event"
                        :value="subgroup"
                        :readonly="!team"
                    />
                </v-col>
            </v-row>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn depressed @click="hide" :disabled="loading">
                    {{ $t('common.cancel') }}
                </v-btn>
                <v-btn depressed color="primary" class="black--text" @click="checkAssignedRoutines" :disabled="isDisabled" :loading="loading">
                    {{ $t('common.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <select-replace-assigned-routines-dates-dialog
            :assigned-routines-in-same-date="assignedRoutinesInSameDate"
            @confirm="onConfirmReplace($event)"
            @cancel="onCancelReplace"
            ref="replaceRoutinesDates"
        />
    </v-menu>
</template>

<script>
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import {db} from "@/utils/Firebase";
import SelectReplaceAssignedRoutinesDatesDialog
    from "@/components/coach/routines/SelectReplaceAssignedRoutinesDatesDialog";
import {
    addAssignedRoutinesGroup,
    deleteAssignedRoutinesGroup,
    getGroupAssignedRoutines,
    hasAssignedRoutineInDate
} from "@/utils/Exercises";

export default {
    name: "SelectTeamStudentMenu",
    components: {SelectReplaceAssignedRoutinesDatesDialog, KimsaCombobox},
    props: {
        teams: {
            type: Array,
            default: () => [],
        },
        studentId: {
            type: String,
            default: null
        },
        studentName: {
            type: String,
            default: null
        },
        currentTeam: {
            type: Object,
            default: () => {
            },
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            menu: false,
            loading: false,
            team: null,
            subgroup: null,
            groupRoutines: [],
            assignedRoutinesInSameDate: [],
        }
    },
    computed: {
        isDisabled(vm = this) {
            if(vm.team === undefined) {
                return false
            }
            else if(vm.currentTeam) {
                return !vm.subgroup
            } else {
                return !!(!vm.team || !vm.subgroup)
            }
        },
        findTeam(vm = this) {
            return vm.teams.find(team => team.id === vm.currentTeam?.id || null) || null
        },
        findGroup(vm = this) {
            return vm.findTeam?.groups?.find(group => group.id === vm.currentTeam?.subgroupId || null) || null
        },
        teamName(vm = this) {
            return vm.findTeam?.name || vm.$t('common.without_team')
        },
        subgroupName(vm = this) {
            return vm.findGroup?.name ? `- ${vm.findGroup?.name}` : ''
        },
        subgroups(vm = this) {
            return vm.team?.groups || []
        }
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    methods: {
        hide() {
            const vm = this
            vm.menu = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.team = null
            vm.subgroup = null
            vm.groupRoutines = []
            vm.assignedRoutinesInSameDate = []
        },
        async checkAssignedRoutines() {
            const vm = this
            vm.loading = true

            vm.groupRoutines = await getGroupAssignedRoutines(vm.subgroup.id, vm.team.id)
            console.log('groupRoutines', vm.groupRoutines)

            if(vm.groupRoutines.length === 0) return vm.save()

            let assignedRoutinesInSameDate = []
            for (let routine of vm.groupRoutines) {
                let assignedRoutineInDate = await hasAssignedRoutineInDate(vm.studentId, routine.rawDate.full)
                if(assignedRoutineInDate.has) {
                    assignedRoutinesInSameDate.push({
                        assignedRoutineId: assignedRoutineInDate.id,
                        date: vm.$moment(routine.rawDate.full).format('DD-MM-YYYY'),
                        replace: false,
                    })
                }
            }

            console.log('assignedRoutinesInSameDate', assignedRoutinesInSameDate)

            if(assignedRoutinesInSameDate.length > 0) {
                vm.assignedRoutinesInSameDate = assignedRoutinesInSameDate
                vm.$refs.replaceRoutinesDates.show()
            } else {
                for (let routine of vm.groupRoutines) {
                    await addAssignedRoutinesGroup({...routine, date: routine.rawDate.full}, [vm.studentId])
                }
                await vm.save()
            }
        },
        async onConfirmReplace(toReplace) {
            const vm = this
            if(toReplace.length > 0) {
                let toDelete = toReplace.filter(ar => ar.replace).map(ar => {return ar.assignedRoutineId})
                if(toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)

                let skipRoutinesDates = toReplace.filter(ar => !ar.replace).map(ar => {return ar.date})
                for (let routine of vm.groupRoutines) {
                    let currentDate = vm.$moment(routine.rawDate.full).format('DD-MM-YYYY')
                    let skip = skipRoutinesDates.includes(currentDate)
                    if(!skip) await addAssignedRoutinesGroup({...routine, date: routine.rawDate.full}, [vm.studentId])
                }
            }
            await vm.save()

        },
        async save() {
            const vm = this
            vm.loading = true

            let students = [...vm.subgroup.students, vm.studentId]

            let groups = [...vm.team.groups]
            let findGroupIndex = vm.team?.groups?.findIndex(group => group.id === vm.subgroup?.id || null)
            groups[findGroupIndex].students = students

            await db.collection('users').doc(vm.studentId).update({team: {id: vm.team.id, subgroupId: vm.subgroup.id}})
            await db.collection('teams').doc(vm.team.id).update({groups: groups})

            vm.loading = false
            vm.$emit('save', vm.studentId)
            vm.hide()
        },
        setDefaults() {
            const vm = this
            if (!vm.currentTeam) return
            vm.team = {id: vm.findTeam?.id || null, ...vm.findTeam}
            let findSubgroup = vm.team?.groups?.find(group => group.id === vm.currentTeam.subgroupId) || null
            if (!findSubgroup) return
            vm.subgroup = {id: findSubgroup.id, ...findSubgroup}
        },
        onCancelReplace() {
            const vm = this
            vm.loading = false
        }
    },
    watch: {
        currentTeam() {
            this.setDefaults()
        },
        team(val) {
            if(this.currentTeam?.id !== val?.id) this.subgroup = null
        }
    }
}
</script>

<style scoped>

</style>